import Header from '../components/Header'
import Footer from '../components/Footer'

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="card">
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Terms of Service
          </h1>
          <p className="text-gray-400 text-sm mb-8">Last updated: February 5, 2026</p>

          <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-white mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using ScamView ("Service"), you agree to be bound by these Terms of Service.
                If you do not agree, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">2. Description of Service</h2>
              <p>
                ScamView provides AI-powered fraud detection services that analyze emails, messages, and contact
                information to identify potential recruitment scams. The Service includes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Free basic scans with limited daily usage</li>
                <li>Premium subscription plans with advanced features</li>
                <li>Community reporting and intelligence sharing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">3. User Accounts</h2>
              <p>You are responsible for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access</li>
              </ul>
              <p className="mt-2">
                You must provide accurate, current information when creating an account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">4. Acceptable Use</h2>
              <p>You agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the Service for illegal purposes</li>
                <li>Submit false or misleading information</li>
                <li>Attempt to circumvent usage limits or security measures</li>
                <li>Reverse engineer or copy our technology</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Distribute malware or spam</li>
                <li>Scrape or automate access beyond API terms</li>
                <li>
                  Submit inputs designed to probe, map, or infer how our risk scoring works,
                  including systematic or controlled testing intended to reconstruct our
                  detection signals, weightings, or thresholds
                </li>
                <li>
                  Collect, store, or aggregate risk scores, risk levels, detection signals,
                  or any other Service output in bulk, whether manually or automatically
                </li>
                <li>
                  Use any Service output to develop, train, evaluate, or improve any product,
                  dataset, model, or service that competes with ScamView
                </li>
                <li>
                  Use the Service for competitive benchmarking, or publish comparisons of our
                  output against another provider, without our prior written consent
                </li>
                <li>
                  Access the Service on behalf of, or share Service output with, a competitor
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">5. Service Accuracy</h2>
              <p className="font-semibold text-yellow-400">
                IMPORTANT: ScamView provides fraud detection guidance but is not infallible.
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Results are probabilistic, not definitive</li>
                <li>False positives and false negatives may occur</li>
                <li>You should use your own judgment in addition to our analysis</li>
                <li>We are not liable for decisions you make based on our service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">6. Intellectual Property</h2>
              <p>
                All content, features, and functionality of the Service are owned by ScamView and protected
                by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="mt-2">
                You retain ownership of content you submit, but grant us a license to use it to provide
                and improve the Service.
              </p>
              <p className="mt-2">
                Our detection methodology &mdash; including the signals we evaluate, how they are
                weighted, our scoring thresholds, and the data and models used to produce a risk
                score &mdash; constitutes our confidential information and trade secrets. Risk scores,
                risk levels, and signal descriptions are provided to you for your own personal
                decision-making about a specific contact. They are not licensed for redistribution,
                resale, republication, or use as training or evaluation data.
              </p>
              <p className="mt-2">
                We may show which signals contributed to a result. We do not disclose the relative
                weight of any signal, and nothing in the Service should be read as a complete
                description of how a score is calculated.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">7. Subscription and Payment</h2>
              <p>For Premium plans:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Subscriptions are billed monthly or annually</li>
                <li>You authorize automatic recurring charges</li>
                <li>Cancellation stops future billing but no refunds for current period</li>
                <li>Price changes will be communicated 30 days in advance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">8. Termination</h2>
              <p>
                We may suspend or terminate your account if you violate these Terms. You may cancel your
                account at any time through Settings.
              </p>
              <p className="mt-2">
                Upon termination, your right to use the Service ceases immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">9. Disclaimer of Warranties</h2>
              <p className="font-semibold">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
              </p>
              <p className="mt-2">
                We do not guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Uninterrupted or error-free service</li>
                <li>Complete accuracy of fraud detection</li>
                <li>Prevention of all scams</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">10. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SCAMVIEW SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Indirect, incidental, special, or consequential damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Damages arising from use or inability to use the Service</li>
              </ul>
              <p className="mt-2">
                Our total liability shall not exceed the amount you paid in the past 12 months.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">11. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless ScamView from claims arising from your use of
                the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">12. Governing Law</h2>
              <p>
                These Terms are governed by the laws of the State of California, USA, without regard to
                conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">13. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Continued use of the Service
                after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="pt-6 border-t border-white/10">
              <h2 className="text-2xl font-bold text-white mb-3">Contact Us</h2>
              <p>
                Questions about these Terms? Contact us:
              </p>
              <ul className="mt-2 space-y-1">
                <li>Email: legal@scamview.com</li>
                <li>Address: 123 Tech Street, San Francisco, CA 94105</li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
